import { Injectable, inject, PLATFORM_ID } from '@angular/core';
//import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, tap, catchError, of, from, switchMap } from 'rxjs';
import { LoginRequest, LoginResponse, User } from '../interfaces/user.interface';
import { API_CONFIG } from '../config/api.config';
import { SecureStorageService } from './secure-storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly API_URL = API_CONFIG.baseUrl;
  private readonly TOKEN_KEY = API_CONFIG.storage.tokenKey;
  private readonly REFRESH_TOKEN_KEY = API_CONFIG.storage.refreshTokenKey;
  private readonly USER_KEY = API_CONFIG.storage.userKey;

  private http = inject(HttpClient);
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  private secureStorage = inject(SecureStorageService);
  //private isBrowser = isPlatformBrowser(this.platformId);

  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  private initializationComplete = false;
  private initializationPromise: Promise<void> | null = null;
  private logoutChannel: BroadcastChannel | null = null;

  constructor() {
    // NO llamar initializeAuth aquí - causa dependencia circular
    // Se llamará de forma lazy cuando waitForInitialization() sea invocado
    
    // Configurar sincronización de logout entre pestañas
    this.setupCrossTabLogout();
  }
  
  /**
   * Configura la sincronización de logout entre pestañas usando BroadcastChannel
   */
  private setupCrossTabLogout(): void {
    try {
      // Crear canal de comunicación entre pestañas
      this.logoutChannel = new BroadcastChannel('auth-channel');
      
      // Escuchar mensajes de logout de otras pestañas
      this.logoutChannel.onmessage = (event) => {
        if (event.data.type === 'logout') {
          console.log('🔔 Logout detectado en otra pestaña - cerrando sesión local');
          this.currentUserSubject.next(null);
          this.router.navigate(['/login']);
        }
      };
    } catch (error) {
      console.warn('BroadcastChannel no soportado en este navegador:', error);
    }
  }

  /**
   * Espera a que se complete la inicialización del servicio
   * La inicialización se ejecuta la primera vez que se llama este método
   */
  async waitForInitialization(): Promise<void> {
    if (this.initializationComplete) {
      return;
    }
    
    // Si es la primera vez, iniciar la inicialización
    if (!this.initializationPromise) {
      this.initializationPromise = this.initializeAuth();
    }
    
    await this.initializationPromise;
  }

  /**
   * Inicializa la autenticación y migra datos si es necesario
   */
  private async initializeAuth(): Promise<void> {
    console.log('🔐 AuthService: Iniciando autenticación...');
    
    // Si se usan cookies HTTP-only, verificar la sesión con el backend
    if (API_CONFIG.cookie_http_only) {
      console.log('🔐 AuthService: Modo cookie_http_only - verificando con /auth/me');
      try {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          'x-api-key': API_CONFIG.api_key
        });
        
        const user = await this.http.get<{ success: boolean; data: User }>(
          `${this.API_URL}${API_CONFIG.endpoints.auth.me}`,
          { headers, withCredentials: true }
        ).toPromise();
        
        if (user?.success && user.data) {
          console.log('✅ AuthService: Sesión válida - usuario cargado:', user.data.profile.email);
          this.currentUserSubject.next(user.data);
        } else {
          console.warn('⚠️ AuthService: Respuesta inválida del servidor');
          this.currentUserSubject.next(null);
        }
      } catch (error: any) {
        // Si falla la verificación, no hay sesión válida
        console.error('❌ AuthService: Error al verificar sesión:', {
          status: error?.status,
          message: error?.message,
          url: error?.url
        });
        this.currentUserSubject.next(null);
      }
      this.initializationComplete = true;
      return;
    }
    
    // Migrar desde localStorage si existe
    await this.migrateFromLocalStorage();
    
    // Cargar usuario desde almacenamiento seguro
    const user = await this.getUserFromStorage();
    this.currentUserSubject.next(user);
    this.initializationComplete = true;
  }

  /**
   * Migra datos desde localStorage a almacenamiento seguro
   */
  private async migrateFromLocalStorage(): Promise<void> {
    const keysToMigrate = [this.TOKEN_KEY, this.REFRESH_TOKEN_KEY, this.USER_KEY];
    await this.secureStorage.migrateFromLocalStorage(keysToMigrate);
  }

  login(credentials: LoginRequest): Observable<LoginResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-api-key': API_CONFIG.api_key
    });
    
    const options = {
      headers,
      withCredentials: API_CONFIG.cookie_http_only // Incluir cookies si está habilitado
    };
    
    return this.http.post<LoginResponse>(`${this.API_URL}${API_CONFIG.endpoints.auth.login}`, credentials, options)
      .pipe(
        switchMap(response => {
          if (response.success) {
            const normalizedUser: User = {
              ...response.data.user,
              organizadorAccess: response.data.organizadorAccess ?? response.data.user?.organizadorAccess ?? null
            };
            // Si usamos cookies HTTP-only, solo actualizamos el usuario en memoria
            if (API_CONFIG.cookie_http_only) {
              this.currentUserSubject.next(normalizedUser);
              return of(response);
            }
            
            // Si NO usamos cookies, guardamos en almacenamiento local
            return from(this.setSession({
              ...response.data,
              user: normalizedUser
            })).pipe(
              switchMap(() => of(response))
            );
          }
          return of(response);
        })
      );
  }

  logout(): void {
    console.log('🚪 Iniciando logout...');
    
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-api-key': API_CONFIG.api_key
    });
    
    const options = {
      headers,
      withCredentials: API_CONFIG.cookie_http_only
    };
    
    if (API_CONFIG.cookie_http_only) {
      console.log('🚪 Logout con cookies HTTP-only - llamando al servidor...');
      // Con cookies HTTP-only, solo necesitamos llamar al endpoint
      // El servidor limpiará las cookies
      this.http.post(`${this.API_URL}${API_CONFIG.endpoints.auth.logout}`, {}, options)
        .pipe(catchError((error) => {
          console.error('❌ Error al llamar /auth/logout:', error);
          return of(null);
        }))
        .subscribe(() => {
          console.log('✅ Servidor respondió logout - limpiando sesión local');
          this.currentUserSubject.next(null);
          
          // Notificar a otras pestañas que se cerró la sesión
          this.notifyLogoutToOtherTabs();
          
          console.log('📌 Navegando a /login');
          this.router.navigate(['/login']);
        });
    } else {
      console.log('🚪 Logout con tokens - limpiando almacenamiento local...');
      // Sin cookies, necesitamos el refresh token del almacenamiento local
      from(this.getRefreshToken()).subscribe(refreshToken => {
        if (refreshToken) {
          console.log('📝 Enviando refresh token al servidor para invalidación');
          this.http.post(`${this.API_URL}${API_CONFIG.endpoints.auth.logout}`, { refreshToken }, options)
            .pipe(catchError((error) => {
              console.error('❌ Error al invalidar refresh token:', error);
              return of(null);
            }))
            .subscribe();
        } else {
          console.warn('⚠️ No hay refresh token para invalidar');
        }
        
        console.log('🧽 Limpiando sesión del almacenamiento local');
        this.clearSession();
        
        // Notificar a otras pestañas que se cerró la sesión
        this.notifyLogoutToOtherTabs();
        
        console.log('📌 Navegando a /login');
        this.router.navigate(['/login']);
      });
    }
  }
  
  /**
   * Notifica a otras pestañas/ventanas que se cerró la sesión
   */
  private notifyLogoutToOtherTabs(): void {
    try {
      if (this.logoutChannel) {
        this.logoutChannel.postMessage({ type: 'logout' });
        console.log('📡 Notificando logout a otras pestañas');
      }
    } catch (error) {
      console.warn('Error al notificar logout:', error);
    }
  }

  private async setSession(authResult: LoginResponse['data']): Promise<void> {
    // Solo guardar en almacenamiento local si NO usamos cookies HTTP-only
    if (!API_CONFIG.cookie_http_only) {
      await this.secureStorage.set(this.TOKEN_KEY, authResult.accessToken);
      await this.secureStorage.set(this.REFRESH_TOKEN_KEY, authResult.refreshToken);
      await this.secureStorage.set(this.USER_KEY, authResult.user);
    }
    this.currentUserSubject.next(authResult.user);
  }

  private async clearSession(): Promise<void> {
    console.log('🧽 Limpiando sesión...');
    // Solo limpiar almacenamiento local si NO usamos cookies HTTP-only
    if (!API_CONFIG.cookie_http_only) {
      await this.secureStorage.remove(this.TOKEN_KEY);
      await this.secureStorage.remove(this.REFRESH_TOKEN_KEY);
      await this.secureStorage.remove(this.USER_KEY);
    }
    this.currentUserSubject.next(null);
  }

  async getToken(): Promise<string | null> {
    // Si usamos cookies HTTP-only, no necesitamos el token del almacenamiento
    if (API_CONFIG.cookie_http_only) {
      return null;
    }
    return await this.secureStorage.get(this.TOKEN_KEY);
  }

  async getRefreshToken(): Promise<string | null> {
    return await this.secureStorage.get(this.REFRESH_TOKEN_KEY);
  }

  private async getUserFromStorage(): Promise<User | null> {
    return await this.secureStorage.getObject<User>(this.USER_KEY);
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  async isAuthenticated(): Promise<boolean> {
    const token = await this.getToken();
    return !!token;
  }

  /**
   * Versión síncrona de isAuthenticated (usa el estado actual en memoria)
   * Útil para guards y verificaciones rápidas después de la inicialización
   */
  isAuthenticatedSync(): boolean {
    return this.currentUserSubject.value !== null;
  }

  isAdmin(): boolean {
    const user = this.getCurrentUser();
    return user?.role.role?.toLowerCase() === 'admin';
  }

  isOrganizador(): boolean {
    const user = this.getCurrentUser();
    return user?.role.role?.toLowerCase() === 'organizador';
  }

  getRolInternoOrganizador(): string | null {
    const user = this.getCurrentUser();
    if (!user || !this.isOrganizador()) return null;

    const roleInterno = user.organizadorAccess?.rolinterno;
    return roleInterno ? String(roleInterno).toLowerCase().trim() : null;
  }

  getOrganizadorIdFromAccess(): string | null {
    const user = this.getCurrentUser();
    return user?.organizadorAccess?.organizadorid || null;
  }

  hasOrganizadorAccessActivo(): boolean {
    const user = this.getCurrentUser();
    if (!user || !this.isOrganizador()) return false;

    const estadoAcceso = user.organizadorAccess?.estado;
    if (!estadoAcceso) return false;

    return String(estadoAcceso).toLowerCase().trim() === 'activo';
  }

  canAccessEventoDashboard(): boolean {
    return this.isAdmin() || this.hasOrganizadorAccessActivo();
  }

  canAccessEventoInfoBasica(): boolean {
    return this.isAdmin() || this.hasOrganizadorAccessActivo();
  }

  /**
   * Permite gestión completa del evento (tipos-ticket, listas, validadores, ubicaciones, subeventos, funciones).
   * Solo ADMIN o rolinterno=administrador activo. ANALISTA no puede.
   */
  canGestionarEvento(): boolean {
    return this.isAdmin() || this.isOrganizadorAdministradorInterno();
  }

  isOrganizadorAdministradorInterno(): boolean {
    const rolInterno = this.getRolInternoOrganizador();
    return this.hasOrganizadorAccessActivo() && !!rolInterno && ['administrador', 'admin'].includes(rolInterno);
  }

  isOrganizadorAnalistaInterno(): boolean {
    return this.hasOrganizadorAccessActivo() && this.getRolInternoOrganizador() === 'analista';
  }

  canManageOrganizadorUsersAndVendedores(): boolean {
    return this.isAdmin();
  }

  canEditEventoBasico(): boolean {
    // Admin interno puede editar solo descripciones/HTML, no campos estructurales del evento
    return this.isAdmin();
  }

  canEditEventoContenidoHtml(): boolean {
    // Admin interno puede editar HTML y contenido descriptivo
    return this.isAdmin() || this.isOrganizadorAdministradorInterno();
  }

  canEditSubeventoVisibilidad(): boolean {
    return this.isAdmin() || this.isOrganizadorAdministradorInterno();
  }

  canToggleTipoTicketEstado(): boolean {
    return this.isAdmin() || this.isOrganizadorAdministradorInterno();
  }

  canToggleTipoTicketPrecioEstado(): boolean {
    return this.isAdmin() || this.isOrganizadorAdministradorInterno();
  }

  /** Admin interno puede editar imágenes del evento y subeventos */
  canEditEventoImagenes(): boolean {
    return this.isAdmin() || this.isOrganizadorAdministradorInterno();
  }

  /** Solo ADMIN puede crear/eliminar TipoTicket. Admin interno solo puede cambiar estado y duplicar NO */
  canCrearEliminarTipoTicket(): boolean {
    return this.isAdmin();
  }

  /** Solo ADMIN puede duplicar TipoTicket */
  canDuplicarTipoTicket(): boolean {
    return this.isAdmin();
  }

  /** Solo ADMIN puede crear/eliminar TipoTicketPrecio */
  canCrearEliminarTipoTicketPrecio(): boolean {
    return this.isAdmin();
  }

  /** Solo ADMIN puede crear/eliminar Subeventos */
  canCrearEliminarSubevento(): boolean {
    return this.isAdmin();
  }

  /** Solo ADMIN puede crear/eliminar Funciones */
  canCrearEliminarFuncion(): boolean {
    return this.isAdmin();
  }

  /** Solo ADMIN puede crear/eliminar Ubicaciones */
  canCrearEliminarUbicacion(): boolean {
    return this.isAdmin();
  }

  /** Admin interno puede editar campos de subevento (nombre, descripción, HTML, imágenes, visibilidad) */
  canEditSubeventoContenido(): boolean {
    return this.isAdmin() || this.isOrganizadorAdministradorInterno();
  }

  /** Solo ADMIN puede ver/editar configuraciones avanzadas completas; admin interno solo ve pestaña de imágenes */
  canVerConfiguracionesCompletas(): boolean {
    return this.isAdmin();
  }

  canViewReportes(): boolean {
    return this.isAdmin() || this.hasOrganizadorAccessActivo();
  }

  canViewVentas(): boolean {
    return this.isAdmin() || this.hasOrganizadorAccessActivo();
  }

  canViewHistorialCarritos(): boolean {
    return this.isAdmin() || this.hasOrganizadorAccessActivo();
  }

  canViewFunciones(): boolean {
    return this.isAdmin() || this.hasOrganizadorAccessActivo();
  }

  canAccessEventoConfiguracionesAvanzadas(): boolean {
    return this.isAdmin();
  }

  changePassword(currentPassword: string, newPassword: string): Observable<{ success: boolean; message: string }> {
    if (API_CONFIG.cookie_http_only) {
      // Usar cookies HTTP-only
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'x-api-key': API_CONFIG.api_key
      });

      const options = {
        headers,
        withCredentials: true
      };

      return this.http.post<{ success: boolean; message: string }>(
        `${this.API_URL}${API_CONFIG.endpoints.auth.changePassword}`,
        { currentPassword, newPassword },
        options
      );
    } else {
      // Usar Bearer token
      return from(this.getToken()).pipe(
        switchMap(token => {
          const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'x-api-key': API_CONFIG.api_key,
            'Authorization': `Bearer ${token}`
          });

          const options = {
            headers,
            withCredentials: false
          };

          return this.http.post<{ success: boolean; message: string }>(
            `${this.API_URL}${API_CONFIG.endpoints.auth.changePassword}`,
            { currentPassword, newPassword },
            options
          );
        })
      );
    }
  }
}
